import React, { useEffect, useState, useMemo } from "react";
import { PackageCheck, CheckCircle2, ClipboardCheck, Camera, X, AlertTriangle, Check } from "lucide-react";
import api from "../lib/api";
import { Card, PageHeader, PageLoader, Badge, Button, Modal } from "../lib/ui";
import { useToast } from "../lib/toast";
import { fmtNum, fmtUnit, orderLabel } from "../lib/constants";
import { ProductAvatar } from "../components/visual";
import { useT } from "../lib/i18n";

export default function Receiving() {
  const toast = useToast();
  const { t } = useT();
  const [orders, setOrders] = useState(null);
  const [recvOrder, setRecvOrder] = useState(null);
  const [rows, setRows] = useState({});
  const [photos, setPhotos] = useState([]);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/orders").then(({ data }) => setOrders(data.filter((o) => o.status === "DELIVERED")));
  useEffect(() => { load(); }, []);

  const diffCount = useMemo(
    () => Object.values(rows).filter((v) => Number(v.received) !== Number(v.expected)).length,
    [rows]
  );

  if (!orders) return <PageLoader />;

  const receiveAll = async (o) => {
    try { await api.post(`/orders/${o.id}/receive`, { receive_all: true, photos: [], discrepancies: [] }); toast.success(t("Order received in full")); load(); }
    catch (e) { toast.error(e.response?.data?.detail || t("Failed")); }
  };

  const openReceive = (o) => {
    const r = {};
    o.items.forEach((it) => {
      const exp = it.delivered_quantity ?? it.ordered_quantity;
      r[it.product_id] = {
        name: it.product_name, unit: it.unit, image: it.image,
        ordered: it.ordered_quantity, expected: exp, received: exp, issue: "short", match: true,
      };
    });
    setRows(r); setPhotos([]); setNotes(""); setRecvOrder(o);
  };

  const setMatch = (pid, isMatch) => setRows((d) => ({
    ...d, [pid]: { ...d[pid], match: isMatch, received: isMatch ? d[pid].expected : d[pid].received },
  }));

  const readPhotos = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach((f) => {
      const reader = new FileReader();
      reader.onload = () => setPhotos((p) => [...p, reader.result]);
      reader.readAsDataURL(f);
    });
  };

  const submitReceive = async () => {
    const discrepancies = Object.entries(rows)
      .filter(([, v]) => Number(v.received) !== Number(v.expected))
      .map(([product_id, v]) => ({
        product_id, expected_quantity: Number(v.expected), received_quantity: Number(v.received),
        issue_type: Number(v.received) < Number(v.expected) ? v.issue : "over", note: notes || null,
      }));
    const receive_all = discrepancies.length === 0;
    setBusy(true);
    try {
      await api.post(`/orders/${recvOrder.id}/receive`, { receive_all, photos, discrepancies, notes });
      toast.success(receive_all ? t("Order received in full") : `${t("Received with")} ${discrepancies.length} ${t("discrepancy item(s)")}`);
      setRecvOrder(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || t("Failed")); }
    finally { setBusy(false); }
  };

  return (
    <div className="animate-fade-up pb-24 lg:pb-2">
      <PageHeader title={t("Receiving")} subtitle={t("Inspect, receive in full or partial, attach photo evidence")} icon={PackageCheck} />

      {orders.length === 0 ? (
        <Card className="flex flex-col items-center gap-2 py-12 text-center" data-testid="receiving-empty">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-500"><CheckCircle2 size={30} /></div>
          <p className="font-bold text-ink">{t("Nothing to receive right now.")}</p>
          <p className="text-sm text-ink-mute">{t("Deliveries from the central warehouse will appear here.")}</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {orders.map((o) => (
            <Card key={o.id} className="p-4" data-testid={`receive-${o.order_no}`}>
              <div className="flex items-start gap-3">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-blue-50 text-blue-600"><PackageCheck size={24} /></div>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-heading text-base font-extrabold text-ink">{orderLabel(o)}</p>
                  <p className="truncate text-xs text-ink-mute">{o.order_no} · {o.items.length} {t("items")}</p>
                </div>
                <Badge tone="success">{t("Delivered")}</Badge>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 border-t border-line pt-3">
                <Button size="sm" onClick={() => openReceive(o)} data-testid={`inspect-${o.order_no}`}><ClipboardCheck size={15} /> {t("Check & Receive")}</Button>
                <Button size="sm" variant="outline" onClick={() => receiveAll(o)} data-testid={`receive-all-${o.order_no}`}><CheckCircle2 size={15} /> {t("Receive All")}</Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal open={!!recvOrder} onClose={() => setRecvOrder(null)} title={`${t("Verify Goods")} · ${recvOrder?.order_no}`} size="lg"
        footer={<><Button variant="outline" onClick={() => setRecvOrder(null)}>{t("Cancel")}</Button><Button onClick={submitReceive} loading={busy} data-testid="submit-receive"><Check size={16} /> {t("Confirm Receiving")}</Button></>}>
        <div className={`mb-3 flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-semibold ${diffCount === 0 ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`} data-testid="receive-summary">
          {diffCount === 0 ? <><CheckCircle2 size={16} /> {t("All items match — ready to receive.")}</> : <><AlertTriangle size={16} /> {diffCount} {t("item(s) with a difference")}</>}
        </div>
        <p className="mb-3 text-xs text-ink-soft">{t("Tap each item to confirm it matches the delivery.")}</p>

        <div className="space-y-2.5" data-testid="receive-items">
          {Object.entries(rows).map(([pid, v]) => {
            const short = Number(v.received) < Number(v.expected);
            return (
              <div key={pid} className={`rounded-2xl border p-3 transition-colors ${v.match ? "border-line bg-white" : "border-amber-300 bg-amber-50/50"}`} data-testid={`recv-card-${v.name}`}>
                <div className="flex items-center gap-3">
                  <ProductAvatar image={v.image} name={v.name} size="sm" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-bold text-ink">{v.name}</p>
                    <p className="text-xs text-ink-soft">{t("Ordered")} {fmtNum(v.ordered)} · {t("Delivered")} <b>{fmtNum(v.expected)}</b> {fmtUnit(v.unit)}</p>
                  </div>
                </div>

                {/* Sesuai / Ada Selisih segmented toggle */}
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <button onClick={() => setMatch(pid, true)} data-testid={`recv-match-${v.name}`}
                    className={`inline-flex items-center justify-center gap-1.5 rounded-xl border-2 px-3 py-2 text-sm font-bold transition-all ${v.match ? "border-emerald-500 bg-emerald-500 text-white" : "border-line bg-white text-ink-soft"}`}>
                    <CheckCircle2 size={16} /> {t("Matches")}
                  </button>
                  <button onClick={() => setMatch(pid, false)} data-testid={`recv-diff-${v.name}`}
                    className={`inline-flex items-center justify-center gap-1.5 rounded-xl border-2 px-3 py-2 text-sm font-bold transition-all ${!v.match ? "border-amber-500 bg-amber-500 text-white" : "border-line bg-white text-ink-soft"}`}>
                    <AlertTriangle size={16} /> {t("Has Difference")}
                  </button>
                </div>

                {!v.match && (
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <div><label className="cf-label">{t("Actual Received")}</label>
                      <input type="number" value={v.received} onChange={(e) => setRows((d) => ({ ...d, [pid]: { ...d[pid], received: e.target.value } }))} className="cf-input" data-testid={`recv-qty-${v.name}`} /></div>
                    {short && (
                      <div><label className="cf-label">{t("Issue")}</label>
                        <select value={v.issue} onChange={(e) => setRows((d) => ({ ...d, [pid]: { ...d[pid], issue: e.target.value } }))} className="cf-input" data-testid={`recv-issue-${v.name}`}>
                          <option value="short">{t("Short")}</option><option value="missing">{t("Missing")}</option><option value="damaged">{t("Damaged")}</option>
                        </select></div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-4">
          <label className="cf-label">{t("Photo Evidence (optional)")}</label>
          <label className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-line py-3 text-sm font-semibold text-ink-soft hover:bg-stone-50" data-testid="receive-photo-label">
            <Camera size={16} /> {t("Add photos")}
            <input type="file" accept="image/*" multiple onChange={readPhotos} className="hidden" data-testid="receive-photo" />
          </label>
          {photos.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {photos.map((p, i) => (
                <div key={i} className="relative">
                  <img src={p} alt="" className="h-16 w-16 rounded-lg object-cover" />
                  <button onClick={() => setPhotos((arr) => arr.filter((_, idx) => idx !== i))} className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-danger text-white"><X size={12} /></button>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="mt-3"><label className="cf-label">{t("Notes (optional)")}</label><textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="cf-input" placeholder={t("Condition, packaging, remarks...")} data-testid="receive-notes" /></div>
      </Modal>
    </div>
  );
}
